/* RESPONSIBLE TEAM: team-reporting */

import { ErrorBoundary } from 'react-error-boundary';
import { Chart } from '@/features/reporting-charts';
import { captureException } from '@/utils/sentry';
import { isChartTypeSupported } from '@/features/reporting-charts/supported-types';
import { BaseChart } from '@/features/report-snapshots/components/charts/base-chart';
import { ChartTitle } from '@/features/report-snapshots/components/charts/chart-title';
import type {
  TabularSchema,
  ChartDefinition,
  ChartSchema,
  Timezone,
  DateRange,
} from '@/features/report-snapshots/api/schema';
import type { SchemaMode } from '@/features/report-snapshots/utils/schema-mode';
import type { ChartType } from '@/features/report-snapshots/types';
import type {
  ChartDataResponse,
  ChartConfig,
  VisualizationType,
  DateRange as ReportingChartsDateRange,
} from '@/features/reporting-charts/types';
import {
  computeDateRange,
  getComparisonDateRanges,
} from '@/features/report-snapshots/formatters/date-range-formatter';

interface ChartRendererProps {
  schemaMode: SchemaMode;
  visualizationType: ChartType;
  tabularSchemas: TabularSchema[];
  legacySchemas: ChartSchema[];
  chartDefinition: ChartDefinition;
  timezone: Timezone;
  dateRange: DateRange;
}

export function ChartRenderer({
  schemaMode,
  visualizationType,
  tabularSchemas,
  legacySchemas,
  chartDefinition,
  timezone,
  dateRange,
}: ChartRendererProps) {
  // Map ChartType to VisualizationType before checking support
  const mappedType = mapVisualizationType(visualizationType);
  const shouldUseNewRenderer =
    schemaMode === 'tabular' &&
    mappedType !== null &&
    isChartTypeSupported(mappedType) &&
    tabularSchemas.length > 0;

  if (!shouldUseNewRenderer) {
    return (
      <BaseChart
        chartType={visualizationType}
        chartSchemas={legacySchemas}
        chartDefinition={chartDefinition}
        timezone={timezone}
        dateRange={dateRange}
      />
    );
  }

  const chartData = adaptTabularToChartData(tabularSchemas);
  const chartConfig = buildChartConfig(chartDefinition);

  // Fall back to legacy if config mapping fails (unsupported visualization type)
  if (!chartConfig) {
    return (
      <BaseChart
        chartType={visualizationType}
        chartSchemas={legacySchemas}
        chartDefinition={chartDefinition}
        timezone={timezone}
        dateRange={dateRange}
      />
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <BaseChart
          chartType={visualizationType}
          chartSchemas={legacySchemas}
          chartDefinition={chartDefinition}
          timezone={timezone}
          dateRange={dateRange}
        />
      }
      onError={(error) => {
        captureException(error, {
          tags: {
            feature: 'reporting-charts',
            component: 'ChartRenderer',
            visualizationType: mappedType,
            errorType: 'render',
          },
          fingerprint: ['reporting-charts', 'render', mappedType],
        });
      }}
    >
      <div className="flex flex-col flex-1 min-h-0">
        <ChartTitle chartDefinition={chartDefinition} />
        <div className="flex-1 min-h-0">
          <Chart
            data={chartData}
            config={chartConfig}
            timezone={timezone.ianaName}
            dateRange={buildDateRange(dateRange, timezone)}
            className="h-full w-full"
          />
        </div>
      </div>
    </ErrorBoundary>
  );
}

function adaptTabularToChartData(schemas: TabularSchema[]): ChartDataResponse {
  return schemas as ChartDataResponse;
}

const SNAPSHOT_TO_REPORTING_CHARTS: Partial<Record<ChartType, VisualizationType>> = {
  // Types that match directly
  counter: 'counter',
  line: 'line',
  area: 'area',
  tabular: 'tabular',
  heatmap: 'heatmap',
  banner: 'banner',
  'data-table': 'data-table',
  // Types that need reverse mapping (snapshot → API/reporting-charts)
  column: 'bar',
  bar: 'horizontal-bar',
  pie: 'donut',
  combo: 'column-line',
};

function mapVisualizationType(snapshotType: ChartType): VisualizationType | null {
  return SNAPSHOT_TO_REPORTING_CHARTS[snapshotType] ?? null;
}

// Maps snapshot time intervals to reporting-charts supported intervals.
function mapTimeInterval(interval: string | null): ChartConfig['viewByTimeInterval'] {
  if (
    interval === 'hour' ||
    interval === 'day' ||
    interval === 'week' ||
    interval === 'month' ||
    interval === 'day_of_week' ||
    interval === 'hour_of_day'
  ) {
    return interval;
  }
  return undefined;
}

function buildDateRange(
  dateRange: DateRange,
  timezone: Timezone,
): ReportingChartsDateRange | undefined {
  const currentRange = computeDateRange(dateRange, timezone);
  if (!currentRange) return undefined;

  const comparisonDates = getComparisonDateRanges(dateRange, timezone);

  return {
    current: currentRange,
    previous: comparisonDates
      ? { start: comparisonDates.comparisonStart, end: comparisonDates.comparisonEnd }
      : undefined,
  };
}

/**
 * Builds ChartConfig from ChartDefinition.
 *
 * Returns null for unmapped visualization types, which signals ChartRenderer
 * to fall back to legacy rendering.
 */
function buildChartConfig(def: ChartDefinition): ChartConfig | null {
  const visualizationType = mapVisualizationType(def.visualizationType as ChartType);

  if (!visualizationType) {
    return null;
  }

  return {
    visualizationType,
    title: def.title ?? undefined,
    viewBy: def.viewBy ?? undefined,
    viewByDisplay: def.viewByDisplay ?? undefined,
    viewByTimeInterval: mapTimeInterval(def.viewByTimeInterval),
    segmentBy: def.segmentBy ?? undefined,
    segmentByDisplay: def.segmentByDisplay ?? undefined,
    segmentByTimeInterval: mapTimeInterval(def.segmentByTimeInterval),
    stacked: def.stacked,
    chartSeries: def.chartSeries.map((s) => ({
      metricId: s.metricId,
      metricDisplayName: s.metricDisplayName ?? undefined,
      aggregation: s.aggregation ?? undefined,
      label: s.label ?? undefined,
      showDataLabels: s.showDataLabels,
    })),
    visualizationOptions: {
      showRelativeValues: def.visualizationOptions.showRelativeValues,
      showSummaryRow: def.visualizationOptions.showSummaryRow,
      showTimeComparison: def.visualizationOptions.showTimeComparison,
      target: def.visualizationOptions.target,
    },
  };
}
